import React, { useState } from "react";
import axios from "axios";
import PublicHeader from "./PublicHeader";

// --- Password strength helpers (shared rules with RegistrationForm) ---
function passwordIssues(pw = "", email = "") {
  const issues = [];
  const local = (email || "").split("@")[0] || "";
  if (pw.length < 12) issues.push("At least 12 characters");
  if (!/[a-z]/.test(pw)) issues.push("At least one lowercase letter");
  if (!/[A-Z]/.test(pw)) issues.push("At least one uppercase letter");
  if (!/[0-9]/.test(pw)) issues.push("At least one number");
  if (!/[^A-Za-z0-9\s]/.test(pw)) issues.push("At least one symbol");
  if (/\s/.test(pw)) issues.push("No spaces");
  if (local && pw.toLowerCase().includes(local.toLowerCase())) {
    issues.push("Must not contain your email username");
  }
  return issues;
}
const isStrongPassword = (pw, email) => passwordIssues(pw, email).length === 0;

function getRandomIndex(max) {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] % max;
  }
  return Math.floor(Math.random() * max);
}

function shufflePassword(chars) {
  const arr = [...chars];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = getRandomIndex(i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.join("");
}

function generateStrongPassword(length = 16) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + numbers + symbols;

  const requiredChars = [
    upper[getRandomIndex(upper.length)],
    lower[getRandomIndex(lower.length)],
    numbers[getRandomIndex(numbers.length)],
    symbols[getRandomIndex(symbols.length)],
  ];

  while (requiredChars.length < length) {
    requiredChars.push(all[getRandomIndex(all.length)]);
  }

  return shufflePassword(requiredChars);
}

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_BACKEND_URL &&
    String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== "undefined" &&
    String(window.location.hostname).indexOf(
      "ai-translation-frontend-sandbox"
    ) !== -1)
    ? "https://ai-translation-backend-sandbox.onrender.com"
    : "https://ai-translation-backend.onrender.com");

const ResetPasswordShell = ({ children }) => (
  <div className="mx-auto px-4" data-testid="reset-password-page">
    <PublicHeader />

    {children}
  </div>
);


const ResetPassword = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");


  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("");

  // Show EULA UI only if backend requires it
  const [needEula, setNeedEula] = useState(false);
  const [eulaAgreed, setEulaAgreed] = useState(false);

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogoClick = () => {
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  };

  const handleSuggestPassword = () => {
    const suggestedPassword = generateStrongPassword(16);
    setNewPassword(suggestedPassword);
    setConfirmPassword(suggestedPassword);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setError("");
    setErrorType("");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setErrorType("");

    if (!newPassword || !confirmPassword) {
      setErrorType("missing-fields");
      setError("Please fill in all fields.");
      return;
    }

    const issues = passwordIssues(newPassword, "");
    if (issues.length) {
      setErrorType("weak-password");
      setError(
        "Please choose a stronger password:\n• " + issues.join("\n• ")
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorType("password-mismatch");
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setErrorType("missing-token");
      setError("Reset link is missing or invalid.");
      return;
    }

    // Build request body; only include eula_agreed if backend already asked for it
    const body = {
      token,
      new_password: newPassword,
      ...(needEula && eulaAgreed ? { eula_agreed: true } : {}),
    };

    if (needEula && !eulaAgreed) {
      setErrorType("eula-required");
      setError(
        "You must agree to the End User License Agreement (EULA) to continue."
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/reset-password`, body);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "EULA acceptance required.") {
        // Backend demanded EULA; reveal the checkbox and message
        setNeedEula(true);
        setErrorType("eula-required");
        setError(
          "You must agree to the End User License Agreement (EULA) to continue."
        );
      } else {
        setErrorType(
          detail === "Reset token is invalid or expired."
            ? "invalid-token"
            : detail
              ? "backend-error"
              : "fallback-error"
        );
        setError(
          detail ||
            "Password reset failed. Please try again or request a new link."
        );
      }
    } finally {
      setLoading(false);
    }
  };
  if (done) {
    return (
      <ResetPasswordShell>
        <div className="max-w-md mx-auto p-6 bg-white rounded-2xl">
          <h2
            className="text-xl font-bold mb-4 text-center"
            data-testid="reset-password-success-heading"
          >
            Password Reset Successful
          </h2>
          <p className="mb-4 text-center" data-testid="reset-password-success-message">
            You can now{" "}
            <a
              href="/login"
              className="underline hover:opacity-90"
              style={{ color: "#00a7e1" }}
              data-testid="reset-password-login-link"
            >
              log in
            </a>{" "}
            with your new password.
          </p>
        </div>
      </ResetPasswordShell>
    );
  }
  return (
    <ResetPasswordShell>
      <div className="max-w-md mx-auto p-6 bg-white rounded-2xl">
      <h2
        className="text-xl font-bold mb-4 text-center"
        data-testid="reset-password-heading"
      >
        Reset Your Password
      </h2>
      <form onSubmit={handleReset} className="flex flex-col gap-4">
        {/* New password */}
        <div className="flex items-center gap-2">
          <label className="w-36 text-right" htmlFor="password">
            Password:
          </label>
          <div className="flex flex-1 items-center gap-2">
            <input
              id="password"
              data-testid="reset-password-password"
              type={showPassword ? "text" : "password"}
              placeholder="New password"
              className="p-2 border rounded flex-1"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={12}
              required
            />
            <button
              type="button"
              data-testid="reset-password-toggle-password"
              onClick={() => setShowPassword((v) => !v)}
              className="px-2 py-1 border rounded flex items-center justify-center"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <img
                src={showPassword ? "/show_password.png" : "/hide_password.png"}
                alt={showPassword ? "Hide password" : "Show password"}
                className="h-4 w-4"
              />
            </button>
          </div>
        </div>

        <div className="flex justify-end -mt-2">
          <button
            type="button"
            data-testid="reset-password-suggest"
            onClick={handleSuggestPassword}
            className="text-sm underline font-semibold hover:opacity-90"
            style={{ color: "#00a7e1" }}
          >
            Suggest Strong Password
          </button>
        </div>

        {/* Confirm password */}
        <div className="flex items-center gap-2">
          <label className="w-36 text-right" htmlFor="confirm-password">
            Confirm Password:
          </label>
          <div className="flex flex-1 items-center gap-2">
            <input
              id="confirm-password"
              data-testid="reset-password-confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              className="p-2 border rounded flex-1"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={12}
              required
            />
            <button
              type="button"
              data-testid="reset-password-toggle-confirm"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="px-2 py-1 border rounded flex items-center justify-center"
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
            >
              <img
                src={
                  showConfirmPassword
                    ? "/show_password.png"
                    : "/hide_password.png"
                }
                alt={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                className="h-4 w-4"
              />
            </button>
          </div>
        </div>

        {/* Live password strength guidance (same behavior as WorkflowBuilder) */}
        {newPassword && (
          <div className="mt-1 text-xs">
            {isStrongPassword(newPassword, "") ? (
              <div
                style={{ color: "#94c83d" }}
                data-testid="reset-password-strong"
              >
                Strong password ✅
              </div>
            ) : (
              <ul
                className="list-disc ml-5 text-red-700"
                data-testid="reset-password-strength-issues"
              >
                {passwordIssues(newPassword, "").map((msg) => (
                  <li key={msg} data-testid="reset-password-strength-issue">{msg}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Passwords mismatch message (live) */}
        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <div
            className="mt-1 text-xs text-red-700"
            aria-live="polite"
            data-testid="reset-password-live-mismatch"
          >
            Passwords do not match.
          </div>
        )}

        {/* EULA section appears ONLY if backend asked for it */}
        {needEula && (
          <div className="flex items-start gap-2">
            <input
              id="eula"
              data-testid="reset-password-eula"
              type="checkbox"
              checked={eulaAgreed}
              onChange={(e) => setEulaAgreed(e.target.checked)}
            />
            <label htmlFor="eula" className="text-sm">
              I agree to the{" "}
              <a
                className="underline"
                style={{ color: "#00a7e1" }}
                href="https://app.termly.io/policy-viewer/policy.html?policyUUID=d083b4a4-7dea-4898-b51e-e34e14d5a432"
                target="_blank"
                rel="noreferrer"
                data-testid="reset-password-eula-link"
              >
                End User License Agreement
              </a>
              .
            </label>
          </div>
        )}

        <button
          type="submit"
          data-testid="reset-password-submit"
          className="text-white py-2 rounded hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: "#00a7e1" }}
          disabled={loading}
        >
          {loading ? "Resetting..." : "Set Password"}
        </button>
        {error && (
          <div
            data-testid="reset-password-error"
            data-error-type={errorType}
            className="text-red-600 text-center"
          >
            {error}
          </div>
        )}
      </form>
      </div>
    </ResetPasswordShell>
  );
};

export default ResetPassword;